/* eslint-disable no-unreachable */
import axios from 'axios';
import DOMPurify from 'dompurify';
import flat from 'flat';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isBuffer from 'lodash/isBuffer';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import omitBy from 'lodash/omitBy';
import transform from 'lodash/transform';
import { validate as uuidValidator } from 'uuid';
// import memoize from 'lodash/memoize';

import ErrorHandlingService from './error-handling-service';
import api from '@/api';
import { DATA_ITEM_KEY_TO_IGNORE } from '@/components/DataInput/DataInputMenu/helpers/data-input-menu.const';

const htmlTags = require('html-tags');

export const addBigNumberSuffix = (n) => {
	if (n < 1e3) return n;
	if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(1) + 'K';
	if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(1) + 'M';
	if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(1) + 'B';
	if (n >= 1e12) return +(n / 1e12).toFixed(1) + 'T';
};

export const removeBigNumberSuffix = (n) => {
	if (n.includes('K')) return +n.replace('K', '') * 1e3;
	if (n.includes('M')) return +n.replace('M', '') * 1e6;
	if (n.includes('B')) return +n.replace('B', '') * 1e9;
	if (n.includes('T')) return +n.replace('T', '') * 1e12;
	return n;
};
export const getReadableAmountInDollars = (number) => formatNumber(number, { style: 'currency', currency: 'USD' });
export const addCommasToNumber = (number) => new Intl.NumberFormat().format(number);
export const formatNumber = (number, options = {}) => {
	const locale = options.locale || 'en-US';
	const formatter = Intl.NumberFormat(locale, options);
	const amountInDollars = formatter.format(number);
	const [roundAmount, cents] = amountInDollars.split('.');
	if (cents === '00') {
		return roundAmount;
	} else {
		return `${roundAmount}.${cents}`;
	}
};

export const camelize = (obj) =>
	transform(obj, (acc, value, key, target) => {
		const camelKey = isArray(target) ? key : camelCase(key);
		acc[camelKey] = isObject(value) ? camelize(value) : value;
	});

export const isBackgroundColor = (color) => color?.includes('#');

export const isRouteOrChildOfRoute = (currentRoute, routeName) =>
	routeName?.includes(currentRoute?.name) || currentRoute?.matched?.some((r) => routeName?.includes(r?.name));

export const applyDrag = (arr, dragResult, item) => {
	const { removedIndex, addedIndex, payload } = dragResult;
	if (removedIndex === null && addedIndex === null) return arr;

	const result = [...arr];
	let itemToAdd = item || payload;

	if (removedIndex !== null) {
		itemToAdd = result.splice(removedIndex, 1)[0];
	}

	if (addedIndex !== null) {
		result.splice(addedIndex, 0, itemToAdd);
	}

	return result;
};

export const getRoutePrefixName = () => (isInstallationRegistrationMode() ? 'Installation' : '');

export const cleanObjectFromNullishValues = (obj) => omitBy(obj, isNullOrUndefined);

export const isInstallationRegistrationMode = () => {
	const Router = require('@/router/index.js');
	const currentRoute = Router.default.history.current;
	return currentRoute.name?.includes('Installation');
};

// {'row.0.value': 'Yossi', 'row.1.value': 'Miri'} =>  {row: [{value: 'Yossi'}, {value: 'Miri'}]}
export const unflatten = (data) => flat.unflatten(data);

export const isFlattenArray = (data) => {
	if (Object(data) !== data) return false;
	if (Array.isArray(data)) return false;
	return Object.keys(data).every((key, index) => key === index);
};

export const filterArrays = (data) => {
	const filteredData = {};
	if (isObject(data)) {
		for (const [key, value] of Object.entries(data)) {
			const isArray = Array.isArray(value);
			if (isArray) {
				filteredData[key] = value;
				continue;
			}
			const isValueFlattenArray = isFlattenArray(value);
			if (isValueFlattenArray) {
				filteredData[key] = Object.values(value);
				continue;
			}
			filteredData[key] = filterArrays(value);
			if (Object.keys(filteredData[key]).length === 0) {
				delete filteredData[key];
			}
		}
	}
	return filteredData;
};

export const flatten = (data, opt = {}, ignoreSimpleValue = false) => {
	const DEFAULT_OPTIONS = {
		safe: true,
	};
	const options = Object.assign({}, DEFAULT_OPTIONS, opt);
	return flat(convertSimpleTypeArrayToObjectArray(data, ignoreSimpleValue), options);
};

// 'My name is {{Name}}'. => 'My name is Noni' when  mapping = {Name: 'Noni'}
export const interpolateString = (toInterpolate, mapping) =>
	toInterpolate.replace(/\{\{[^}]+\}\}/gi, (n) => {
		const value = n.split('{{')[1].split('}}')[0];
		// console.log('n: ', n, 'value', value);
		return get(mapping, value);
	});

export const isMappingString = (str) => typeof str === 'string' && /\{\{[^}]+\}\}/gi.test(str);

export const isNullOrUndefined = function (value) {
	return value === null || value === undefined;
};

export const isNullOrUndefinedOrEmpty = function (value) {
	return isNullOrUndefined(value) || value === '';
};

export const isArrayOfObjects = function (value) {
	return !!value && Array.isArray(value) && value.some((x) => isPlainObject(x));
};

export const isArrayOfSimpleTypes = function (value) {
	return !!value && Array.isArray(value) && value.some((x) => !isPlainObject(x));
};

export const convertSimpleTypeArrayToObjectArray = function (array, ignoreValue) {
	if (!isObject(array) && !isNullOrUndefined(array)) {
		return { [ignoreValue ? DATA_ITEM_KEY_TO_IGNORE : 'value']: array };
	}
	return isArrayOfSimpleTypes(array) ? array.map((value) => ({ value })) : array;
};

export const flattenPreviousSteps = (obj) => {
	const flatPreviousSteps = {};
	for (const [stepUUID, step] of Object.entries(obj)) {
		for (const [stepValueKey, stepValue] of Object.entries(step)) {
			flatPreviousSteps[`${stepUUID}__${stepValueKey}`] = stepValue;
		}
	}
	return flatPreviousSteps;
};

export const propsValidator = function (route, component, extraProps = {}) {
	const props = { ...route.query, ...route.params, ...extraProps };
	for (const key in props) {
		const originalPropValue = props[key];
		if (!component.props?.hasOwnProperty(key)) {
			delete props[key];
			continue;
		}
		if (Array.isArray(component.props[key].type)) {
			continue;
		}
		if (!(originalPropValue instanceof component.props[key].type)) {
			let propValue;
			try {
				const convertedProp = component.props[key].type(originalPropValue);
				if (Number.isNaN(convertedProp)) {
					continue;
				}
				propValue = convertedProp;
			} catch (error) {
				propValue = originalPropValue;
			}
			props[key] = propValue;
		}
	}

	return props;
};
export const extensionPropsValidator = (route = {}, component, extraProps = {}) => {
	const routeParams = { ...(route.params || {}), extensionMode: true };
	return propsValidator({ ...route, params: routeParams }, component, extraProps);
};

export const buildRootStepPreviousStepsObject = (rootNode, flowMappedFields) => ({ [rootNode?.id]: flowMappedFields });

export const filterArraysFromPreviousStepData = (previousStepData) => {
	const filtered = filterArrays(previousStepData);
	const ret = flatten(filtered);
	return ret;
};

export const buildListenerFunction =
	(component, event) =>
	(...args) =>
		component.$emit(event, ...args);

export const buildListenersObject = (component, events) =>
	events.reduce(
		(listeners, listenerFunction) => ({
			...listeners,
			...{ [listenerFunction]: buildListenerFunction(component, listenerFunction) },
		}),
		{}
	);

export const getTwoWayBindingToStoreMixin = (objectName, updateActionName, bindingsHash) => {
	/*
		An implementation of 2 way binding combined with Flux\Redux server update flow:
		1) The Object's property (user_name) is binded in the view (userName)
		2) When userName is updated updateActionName is called to update the propery (user_name) in the server
		3) The store is updated be the response from the server and contains the uppdated object 'objectName' with updated propery (user_name)
		4) The store causes Vue to update objectName (userData) through the "way data flow" principal of Redux
		5) the value displayed on the view (userName) represents the latest value from the server


		e.g. 
		
		'userData' is of the form: {user_id: 1, user_name: 'noni'}

		usage:
		mixins: [getTwoWayBindingToStoreMixin('userData', 'someMethodToUpdateStore', {userName: 'user_name})]
		


		objectName: 'userData' - this.userData needs to exist on the component
		updateActionName: 'updateUserAction' - should be a method that updates the store
		bindingsHash: {user_name: 'userName'} - this.userName is to be accessed from the component
	*/
	const localObjectName = objectName + 'Local';

	const computedToReturn = {};
	Object.keys(bindingsHash).forEach((key) => {
		const objectFieldName = bindingsHash[key];
		computedToReturn[key] = {
			set(value) {
				this[updateActionName]({ [objectFieldName]: value });
			},
			get() {
				return this[localObjectName][objectFieldName];
			},
		};
	});
	return {
		watch: {
			[objectName]: {
				handler(newV) {
					if (newV) {
						this[localObjectName] = newV;
					}
				},
				immediate: true,
				deep: true,
			},
		},
		computed: computedToReturn,

		data() {
			return {
				[localObjectName]: null,
			};
		},
	};
};

export const DEFAULT_NESTING_DELIMITER = '.';
export const DEFAULT_EMPTY_OBJ_REPR = '';
const DEFAULT_ARRAY_REPR_DELIMITER = ', ';

export function flattenPreserveArrays(thing, { delimiter = DEFAULT_NESTING_DELIMITER, emptyObjectRepr = DEFAULT_EMPTY_OBJ_REPR } = {}) {
	/**
	 * Manipulate an array or an object, so it doesn't contain nested objects.
	 * Every array inside the tree, allows another 1 level of objects to exist.
	 */
	if (isArray(thing)) {
		return thing.map((element) => flattenPreserveArrays(element, { delimiter, emptyObjectRepr }));
	} else if (isPlainObject(thing)) {
		const output = {};
		// eslint-disable-next-line no-inner-declarations
		function traverse(obj, prevKey = null) {
			Object.entries(obj).forEach(([key, value]) => {
				const newKey = prevKey ? prevKey + delimiter + key : key;
				if (isArray(value)) {
					output[newKey] = flattenPreserveArrays(value, { delimiter, emptyObjectRepr });
				} else if (isPlainObject(value) && !isBuffer(value)) {
					if (Object.keys(value).length) {
						traverse(value, newKey);
					} else {
						output[newKey] = emptyObjectRepr;
					}
				} else {
					output[newKey] = value;
				}
			});
		}
		traverse(thing);
		return output;
	}
	return thing;
}

export function formatArray(array) {
	return array.join(DEFAULT_ARRAY_REPR_DELIMITER);
}

export function sanitizeHtml(dirtyContent) {
	return DOMPurify.sanitize(dirtyContent, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });
}

export function escapeNonValidHTMLTags(content) {
	content = content?.toString() || '';
	const prefix = '<';
	const encodedPrefix = '&lt;';
	const suffix = '>';
	const encodedSuffix = '&gt;';

	const isContentInsideHtmlTags = content.includes(prefix) && content.includes(suffix);
	if (!isContentInsideHtmlTags) {
		return content;
	}

	const builtContent = content.split(prefix).map((str) => {
		const strArr = str.split(suffix);
		if (strArr.length > 1) {
			const tagNameAndStyle = strArr.shift();
			const tagName = tagNameAndStyle.split(' ')[0];
			const tagBody = strArr[0];
			if (htmlTags.includes(tagName.replace('/', '')) || tagName[0] === '!') {
				return prefix + tagNameAndStyle + suffix + tagBody;
			} else {
				return encodedPrefix + tagNameAndStyle + encodedSuffix + tagBody;
			}
		} else {
			return `${strArr.join('')}`;
		}
	});
	return builtContent.join('');
}

export function isValidHttpUrl(string) {
	let url;

	try {
		url = new URL(string);
	} catch (_) {
		return false;
	}

	return url.protocol === 'http:' || url.protocol === 'https:';
}

export const isUUID = (value) => uuidValidator(value);

export const buildProfileObj = ({
	id: serviceId,
	auth_fields: authFields,
	service_name: serviceName,
	auth_instructions: authInstructions,
	auth_description: authDescription,
	version,
}) => ({
	serviceID: serviceId,
	serviceName,
	fields: authFields,
	title: `Connect ${serviceName} Account`,
	description: authDescription || '', //required
	markdownDescription: authInstructions,
	version,
});

/**
 * converts an array of objects into one single object
 * example:
 * someArray === [{'key': 'abc', 'title': 'the abc value'}, {'key': 'bcd', 'title': 'the bcd value' }]
 * key === 'key'
 * value === 'title'
 * returns === {'abc': 'the abc value', 'bcd': 'the bcd value' }
 * @param {*} someArray - the array
 * @param {*} key - the key field from the object. default is 'id'
 * @param {*} value - [Optional], which key in item is the value to save. If omitted will return the item itself as the value.
 * @returns the converted array
 */
export const convertArrayOfObjectsToObject = (someArray, key = 'id', value = null) =>
	someArray.reduce((object, curItem) => ({ ...object, [curItem[key]]: value ? curItem[value] : curItem }), {});

export const uploadWidgetFile = async ({ file, widgetId, onUploadProgress }) => {
	const uploadParams = await api.getWidgetFieldUploadUrl(widgetId, file.name);
	const extraParams = uploadParams.protected ? {} : { 'x-amz-acl': 'public-read' };
	const postData = new FormData();
	for (const key in uploadParams.s3.fields) {
		postData.append(key, uploadParams.s3.fields[key]);
	}
	postData.append('file', file, file.name);
	await axios.post(uploadParams.s3.url, postData, { ...extraParams, onUploadProgress });
	return uploadParams.file_url;
};
/**
 * Used for uploading user files [not part as of widgets or user facing]
 */
export const uploadFile = async ({ file, onUploadProgress }) => {
	const resp = await api.generateUploadUrl(file.name);
	const uploadParams = resp['url'];
	const extraParams = { 'x-amz-acl': 'public-read' };
	const postData = new FormData();
	for (const key in uploadParams.fields) {
		postData.append(key, uploadParams.fields[key]);
	}
	postData.append('file', file, file.name);
	await axios.post(uploadParams.url, postData, { ...extraParams, onUploadProgress });
	const fileURL = `${uploadParams.url}${uploadParams.fields.key}`;
	return fileURL;
};

export const safeApplyFn = (fn) => {
	// Validates that the service runs only on relevant environments
	// and handles exceptions - we cannot fail because external services\libraries
	try {
		return fn();
	} catch (err) {
		console.error(err);
		return null;
	}
};

export const safeLog = (fn) => ErrorHandlingService.safeLog(fn);

/**
 * This is similar to `convertArrayOfObjectsToObject`, but it reuses the same object for populating.
 * We may consider to migrate to a single function.
 */
export const arrayToObject = (arr, key = 'id') =>
	arr.reduce((acc, item) => {
		if (!(key in item)) {
			throw new Error(`'no key ${key} in ${item}`);
		}
		acc[item[key]] = item;
		return acc;
	}, {});

/* Download a file from a given URL */
export const downloadURI = async (url, name, onSuccess = () => null, onFail = () => null) => {
	try {
		window.open(url);
	} catch (err) {
		onFail(err);
	}
	onSuccess();
};

export const openUrl = async (url, isOpenInNewTab) => {
	try {
		if (!isOpenInNewTab) {
			window.location.href = url;
		} else {
			window.open(url, '_blank');
		}
	} catch (err) {
		console.error(err);
	}
};

export const buildKeyValObject = (keyValArray) => {
	if (!keyValArray) {
		return {};
	}
	return keyValArray.reduce((input, item) => ({ ...input, [item.text]: item.value }), {});
};

export const isValidRedirectURL = (url) => {
	try {
		new URL(url).hostname.includes('ply.io');
		return true;
	} catch (err) {
		return false;
	}
};
